import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme, css } from 'styled-components';
import videojs from 'video.js';
import { VideoPlayer as BaseVideoPlayer } from '@skooldio/skooldio-video-player';

import '@skooldio/skooldio-video-player/dist/skd-videojs.css';
import '@skooldio/skooldio-video-player/dist/fontello.css';
import '@skooldio/skooldio-video-player/dist/animation.css';

import composeRefs from '@seznam/compose-react-refs';

import { useVideoPlayer } from 'Context/VideoPlayer';
import { useComponentSize } from 'Util/Hooks';
import { IS_OVERRIDE_NATIVE_HLS } from 'Util/Capacitors/constant';
import { getAuthorization } from 'Util/Capacitors/utils';
import getConfig, { DEFAULT_DRM_LICENSE_PATH } from 'Util/Config';
import { ref } from 'Util/CustomPropTypes';
import { VideoPlayerContextPropTypes } from 'Context/VideoPlayer/VideoPlayerProvider';

import Overlay, { OverlayPropTypes } from './Overlay';
import { VideoController, VideoBigPlayButton, VideoFullSizeOverlay } from './components';

// Path from the deployment config (videoPlayer.drmLicensePath, od-at-home-deployment). A blank value
// falls back to the default — `includes('')` would put the Bearer token on every VHS request,
// CDN segments included.
const isUriAllowHeaders = (uri) =>
  uri.includes(getConfig().videoPlayer?.drmLicensePath || DEFAULT_DRM_LICENSE_PATH);

// App builds (IS_STORAGE_MODE) authenticate API calls with a Bearer token and do not keep the
// session cookie in sync, while the DRM key endpoint (skooldio-vdo-key) only got the cookie →
// 401 on /license/drm/byteark/playback/key. That endpoint already accepts Authorization
// (loopback.token default keys), so send the Bearer token as well. Web has no stored token → cookie only.
const getBearer = () => {
  const accessToken = getAuthorization();
  return accessToken ? `Bearer ${accessToken}` : undefined;
};

// One shared, mutable header object for every key request. skooldio-video-player captures
// `authHeaders` once at mount and reads its values per request, while VideoBox swaps src in place
// and gets a new video access token per video — the server keeps only the latest one per user, so
// a captured copy goes stale (403) wherever the session cookie is not sent.
const licenseHeaders = {};
const setLicenseHeader = (name, value) => {
  if (value) licenseHeaders[name] = value;
  else delete licenseHeaders[name];
};

// Global fallback: VHS creates a fresh xhr per source and the per-player hook from
// skooldio-video-player is only bound on `loadstart`, so a key request can go out unhooked.
videojs.Vhs.xhr.beforeRequest = (options) =>
  isUriAllowHeaders(options.uri)
    ? {
        ...options,
        withCredentials: true,
        headers: { ...options.headers, ...licenseHeaders },
      }
    : options;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  .video-js {
    font-family: inherit;
  }
  .video-js .vjs-slider.vjs-progress-holder .vjs-play-progress,
  .video-js .vjs-volume-bar.vjs-slider-vertical .vjs-volume-level {
    background-color: ${({ theme: { colors } }) => colors.primary};
  }
  /* icons */
  .video-js .vjs-button > .vjs-icon-placeholder:before {
    font-size: 2.2em;
  }
  .video-js .vjs-menu-button .vjs-playback-rate-value {
    padding-top: 0.09em;
  }
  .video-js .vjs-fullscreen-control.vjs-button > .vjs-icon-placeholder:before {
    padding-top: 0.06em;
  }
  /* time and playback speed text */
  .video-js .vjs-time-control,
  .video-js .vjs-playback-rate .vjs-playback-rate-value {
    font-size: 1.4em;
    font-weight: 400;
  }

  .video-js:not(.vjs-has-started) .vjs-control-bar,
  .video-js div.vjs-control-bar {
    display: ${({ shouldHideVJSControlBar }) => (shouldHideVJSControlBar ? 'none' : 'flex')};
  }

  .video-js .vjs-resize-manager {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    z-index: -1000;
  }

  .video-js .vjs-playback-rate.vjs-menu-button-popup .vjs-menu,
  .video-js .quality-selector-hls-menu-button .vjs-menu {
    width: 8.5em;
  }

  .video-js .vjs-menu-button.vjs-menu-button-popup .vjs-menu li {
    padding: 0.43em 0;
  }
  /* FYI: protect js-progress-control show over custom video overlay*/
  .video-js .vjs-progress-control {
    z-index: 0;
  }

  .video-js .quality-selector-hls-menu.quality-selector-hls-menu-button {
    height: 40px;
  }
`;

const VIDEO_INFO_HEIGHT = 64;
const VIDEO_CONTROLLER_HEIGHT = 91;

const VideoInnerWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const VideoBigPlayButtonWrapper = styled.div`
  ${({ active, isSmallVideo }) => css`
    visibility: ${active ? 'visible' : 'hidden'};
    transition: visibility 0s ease-in-out, opacity 0.5s ease-in-out;
    position: absolute;
    z-index: 1;
    width: ${isSmallVideo ? VideoBigPlayButton.SIZE.NORMAL : VideoBigPlayButton.SIZE.BIG};
    height: ${isSmallVideo ? VideoBigPlayButton.SIZE.NORMAL : VideoBigPlayButton.SIZE.BIG};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}
`;

const VideoInfoWrapper = styled.div`
  height: ${VIDEO_INFO_HEIGHT}px;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: visibility 0s ease-in-out, opacity 0.5s ease-in-out;
  width: 100%;
  position: absolute;
  top: 0;
  z-index: 1;
`;

const VideoControllerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: ${VIDEO_CONTROLLER_HEIGHT}px;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: visibility 0s ease-in-out, opacity 0.5s ease-in-out;
  width: 100%;
  height: ${({ theme, videoWidth }) => (videoWidth <= theme._breakpoints[1] ? '100%' : 'auto')};
  position: absolute;
  bottom: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
  z-index: 1;
`;

const settingIconElementString =
  '<svg fill="white" width="24px" height="24px"><path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" /></svg>';

// `this` is the player in this context
const videoHotkeysConfig = function (event) {
  // `spacebar` key = play/pause
  if (event.which === 32) {
    event.preventDefault();
    if (this.paused()) {
      this.play();
    } else {
      this.pause();
    }
  }
  // `M` key = mute/unmute
  else if (event.which === 77) {
    if (this.muted()) {
      this.muted(false);
    } else {
      this.muted(true);
    }
  }
  // `F` key = enter/exit fullscreen
  else if (event.which === 70) {
    if (this.isFullscreen()) {
      this.exitFullscreen();
    } else {
      this.requestFullscreen();
    }
  }
  // `Right Arrow (->)` key = forward 10 seconds
  else if (event.which === 39) {
    const targetTime = this.currentTime() + 10;
    if (targetTime <= this.duration()) {
      this.currentTime(targetTime);
    } else {
      this.currentTime(this.duration());
    }
  }
  // `Left Arrow (<-)` key = backward 10 seconds
  else if (event.which === 37) {
    const targetTime = this.currentTime() - 10;
    if (targetTime < 0) {
      this.currentTime(0);
    } else {
      this.currentTime(targetTime);
    }
  }
};

const VideoPlayer = ({
  overlayDisplayName,
  overlayPhoneNumber,
  onOverlayCorrupted,
  forwardedRef,
  videoPlayerContext,
  videoAccessToken,
  videoOrigin,
  isVideoJsControl,
  renderCustomVideoInfo,
  renderCustomVideoController,
  renderCustomBigPlayButton,
  renderCustomVideoOverlay,
  isShowInfo,
  isShowControl,
  enableResolutionSwitcher,
  isAutoplay,
  isNative,
  disableFullscreen,
  bigPlayButton,
  showUserOverlay,
  showVideoOverlayOnReady,
  showVideoOverlayOnPause,
  showVideoOverlayOnEnd,
  shouldHideControlOnSmallSize,
  enableDoubleTapsVideoPlayer,
  enableHotkeys,
  ...restProps
}) => {
  const [isShowOverlay, setIsShowOverlay] = useState(false);
  const { _breakpoints } = useTheme();
  const videoWrapperRef = useRef();
  const videoPlayerRef = useRef();
  const clickTimeout = useRef(null);

  const {
    setPlayer,
    player,
    play,
    replay,
    pause,
    videoState,
    VIDEO_STATE,
    userActive,
    setUserActive,
  } = useVideoPlayer();
  const videoSize = useComponentSize(videoWrapperRef);
  const isSmallVideo = useMemo(() => videoSize.width < _breakpoints[0], [videoSize]);

  useEffect(() => {
    const domPlayer = videoPlayerRef?.current ?? null;
    if (domPlayer) {
      setPlayer(domPlayer);
    }
    return () => {
      setPlayer(null);
    };
  }, []);

  useEffect(() => {
    const isVideoReady = showVideoOverlayOnReady && videoState === VIDEO_STATE.READY;
    const isVideoPause = showVideoOverlayOnPause && videoState === VIDEO_STATE.PAUSED;
    const isVideoEnded = showVideoOverlayOnEnd && videoState === VIDEO_STATE.ENDED;
    if (player) {
      if (isVideoReady || isVideoPause || isVideoEnded) {
        setIsShowOverlay(true);
      } else {
        setIsShowOverlay(false);
      }
    }
  }, [
    videoState,
    VIDEO_STATE,
    showVideoOverlayOnReady,
    showVideoOverlayOnPause,
    showVideoOverlayOnEnd,
    player,
  ]);

  let overrideHlsConfig = {};
  if (IS_OVERRIDE_NATIVE_HLS) {
    overrideHlsConfig = {
      html5: {
        nativeAudioTracks: false,
        nativeTextTracks: false,
        hls: {
          overrideNative: true,
        },
      },
    };
  }

  // Video.js options - will be with DEFAULT_VIDEO_OPTIONS
  // See https://docs.videojs.com/tutorial-options.html
  const videoOptions = {
    playbackRates: [0.5, 1.0, 1.25, 1.5, 2.0],
    controls: isVideoJsControl,
    controlBar: {
      children: [...BaseVideoPlayer.DEFAULT_VIDEO_OPTIONS.controlBar.children, 'fullscreenToggle'],
    },
    userActions: {
      hotkeys: enableHotkeys ? videoHotkeysConfig : false,
    },
    bigPlayButton: bigPlayButton,
    autoplay: isAutoplay,
    disableFullscreen: disableFullscreen,

    // disable YouTube top right buttons - "Watch Later" and "Share"
    // but will bring up "Copy Link" instead
    // youtube: { enablePrivacyEnhancedMode: true },

    ...overrideHlsConfig,
  };
  // Only overwrite when this player has a token: a YouTube/plain player renders without one and
  // must not wipe the token of a ByteArk player (it is per user, not per player).
  if (videoAccessToken) {
    setLicenseHeader('skooldio-video-access-token', videoAccessToken);
    setLicenseHeader('skooldio-video-origin', videoOrigin);
  }
  setLicenseHeader('Authorization', getBearer());
  const authHeaders = licenseHeaders;

  const handleClick = (isActive) => (e) => {
    e.preventDefault();
    if (enableDoubleTapsVideoPlayer) {
      if (clickTimeout.current === null) {
        clickTimeout.current = setTimeout(() => {
          setUserActive(!isActive);
          clickTimeout.current = null;
        }, 300);
      }
    } else {
      if (videoState === VIDEO_STATE.PLAY) {
        return pause();
      }
      play();
    }
  };

  const handleDoubleClick = (isActive) => (e) => {
    e.preventDefault();
    if (enableDoubleTapsVideoPlayer) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
      const { left } = e.target.getBoundingClientRect();
      const pointerX = e.clientX - left;
      const leftPart = e.target.clientWidth * 0.33;
      const middlePart = leftPart * 2;
      if (pointerX <= leftPart) {
        return player.currentTime(player.currentTime() - 10);
      } else if (pointerX > leftPart && pointerX < middlePart) {
        if (videoState === VIDEO_STATE.PLAY) {
          return pause();
        }
        return play();
      } else if (pointerX >= middlePart) {
        return player.currentTime(player.currentTime() + 10);
      }
    }
  };

  const isActive =
    !isShowOverlay &&
    ((userActive && videoState !== VIDEO_STATE.LOAD) || videoState === VIDEO_STATE.PAUSED);
  const isShowOverlayBackground =
    isShowInfo ||
    bigPlayButton ||
    isShowControl ||
    showVideoOverlayOnPause ||
    showVideoOverlayOnEnd;
  return (
    <VideoWrapper ref={videoWrapperRef} shouldHideVJSControlBar={shouldHideControlOnSmallSize}>
      <BaseVideoPlayer
        videoOptions={videoOptions}
        // TODO: why restProps and follow with other props down there?
        {...restProps}
        //TODO: migrate to use single ref
        ref={composeRefs(forwardedRef, videoPlayerRef)}
        isUriAllowHeaders={isUriAllowHeaders}
        isCredentials={true}
        authHeaders={authHeaders}
        qualitySelector={{
          isEnable: enableResolutionSwitcher,
          innerElements: settingIconElementString,
        }}
        // seekButtonOptions get object that consist of forward and back key
        // value of these keys can be one of list [10,30]
        seekButtonOptions={{ forward: 10, back: 10 }}
      >
        {showUserOverlay && (
          <Overlay
            displayName={overlayDisplayName}
            phoneNumber={overlayPhoneNumber}
            onOverlayCorrupted={onOverlayCorrupted}
            videoRef={videoPlayerRef}
          />
        )}
        {isShowInfo && (
          <VideoInfoWrapper active={isActive}>
            {typeof renderCustomVideoInfo === 'function' ? renderCustomVideoInfo({ player }) : null}
          </VideoInfoWrapper>
        )}
        {bigPlayButton && (
          <VideoBigPlayButtonWrapper active={isActive} isSmallVideo={isSmallVideo}>
            {typeof renderCustomBigPlayButton === 'function' ? (
              renderCustomBigPlayButton({ player })
            ) : (
              <VideoBigPlayButton
                player={player}
                size={isSmallVideo ? VideoBigPlayButton.SIZE.NORMAL : VideoBigPlayButton.SIZE.BIG}
              />
            )}
          </VideoBigPlayButtonWrapper>
        )}
        {isShowControl && (
          <VideoControllerWrapper
            id="videoControllerWrapper"
            active={isActive}
            videoWidth={videoSize.width}
            onDoubleClick={handleDoubleClick(isActive)}
            onClick={handleClick(isActive)}
          >
            {typeof renderCustomVideoController === 'function' ? (
              <div onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                {renderCustomVideoController({ player })}
              </div>
            ) : (
              <VideoController
                disableFullscreen={disableFullscreen}
                player={player}
                showVideoResolution={enableResolutionSwitcher}
              />
            )}
          </VideoControllerWrapper>
        )}
        {isShowOverlayBackground && (
          <VideoInnerWrapper>
            <div
              style={{ position: 'relative', width: '100%', height: '100%' }}
              onDoubleClick={handleDoubleClick(isActive)}
              onClick={handleClick(isActive)}
            />
          </VideoInnerWrapper>
        )}
        {(showVideoOverlayOnReady || showVideoOverlayOnPause || showVideoOverlayOnEnd) && (
          <VideoFullSizeOverlay
            active={isShowOverlay}
            player={{
              closeOverlay: () => setIsShowOverlay(false),
              openOverlay: () => setIsShowOverlay(true),
              isShowOverlay,
              player,
              play,
              replay,
              pause,
              videoState,
              videoSize,
              VIDEO_STATE,
            }}
            renderCustomVideoOverlay={renderCustomVideoOverlay}
          />
        )}
      </BaseVideoPlayer>
    </VideoWrapper>
  );
};

VideoPlayer.propTypes = {
  forwardedRef: ref,
  videoPlayerContext: VideoPlayerContextPropTypes,

  /* for HTTP Headers */
  videoAccessToken: PropTypes.string,
  videoOrigin: PropTypes.string,

  /* <video controls /> - See https://docs.videojs.com/tutorial-options.html#controls */
  isVideoJsControl: PropTypes.bool,
  /* our custom controls - will disable video.controls */
  isShowControl: PropTypes.bool,
  /* <video autoplay /> */
  isAutoplay: PropTypes.bool,

  /* Overlay props */
  // TODO: rename to enableWatermark
  showUserOverlay: PropTypes.bool,
  /* ANY - String to be shown on watermark */
  overlayDisplayName: OverlayPropTypes.displayName,
  /* ANY - detects DOM watermark user manipulation */
  onOverlayCorrupted: OverlayPropTypes.onOverlayCorrupted,

  // ANY - custom video parts
  // ANY - top SS name, above watermark
  renderCustomVideoInfo: PropTypes.func,
  // TODO: might not need isShowInfo - it just allows renderCustomVideoInfo
  isShowInfo: PropTypes.bool,

  // ANY - middle play button
  renderCustomBigPlayButton: PropTypes.func,
  // IPST - enabled for chromeless youtube player??
  bigPlayButton: PropTypes.bool,

  // ANY - enabled only if isShowControl === true
  renderCustomVideoController: PropTypes.func,

  // a Video Overlay covering the whole player, above everything else
  // enabled only
  renderCustomVideoOverlay: PropTypes.func,

  // ANY - resolution switcher for which custom controller???
  // TODO: there are 2 different custom controls???
  enableResolutionSwitcher: PropTypes.bool,
  // for both VJS default and custom controls
  disableFullscreen: PropTypes.bool,

  // IPST - enabled for chromeless youtube player
  // a workaround for visually blocking youtube related videos
  showVideoOverlayOnReady: PropTypes.bool,
  showVideoOverlayOnPause: PropTypes.bool,
  showVideoOverlayOnEnd: PropTypes.bool,

  // LNO - enabled for double taps function on video player like YouTube behavior
  enableDoubleTapsVideoPlayer: PropTypes.bool,
  // SKD - pass to VJS default controls - videojs-hotkeys
  enableHotkeys: PropTypes.bool,

  // Provided by CapacitorContext
  // from VideoBox level
  // TODO: move to VideoPlayer level?
  isNative: PropTypes.bool,

  // SKD - VideoSlide at certain size cannot contain all controls
  shouldHideControlOnSmallSize: PropTypes.bool,
};

VideoPlayer.defaultProps = {
  isVideoJsControl: false,
};

export default React.forwardRef((props, ref) => <VideoPlayer {...props} forwardedRef={ref} />);
